import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Card,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';

interface Entity {
  id: string;
  /** Основное название карточки. */
  label: string;
  /** Вторая строка с подзаголовком. */
  subline?: string | null;
  /** Третья строка (адрес, контакт) — мелким шрифтом. */
  details?: string | null;
}

interface Props<T extends Entity> {
  opened: boolean;
  onClose: () => void;
  title: string;
  items: T[];
  selectedId?: string | null;
  onSelect: (item: T) => void;
  /** По каким полям ищем (по умолчанию label + subline + details). */
  searchFields?: (item: T) => string[];
  emptyText?: string;
}

/**
 * Универсальный пикер сущности — модалка со списком и поиском.
 * Используется для: «Мои грузы», «Мои контрагенты», «Мои адреса».
 */
export function EntityPicker<T extends Entity>({
  opened,
  onClose,
  title,
  items,
  selectedId,
  onSelect,
  searchFields,
  emptyText = 'Список пуст',
}: Props<T>) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    const fields = searchFields ?? ((it: T) => [it.label, it.subline ?? '', it.details ?? '']);
    return items.filter((it) => fields(it).some((f) => f.toLowerCase().includes(q)));
  }, [search, items, searchFields]);

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="lg">
      <Stack>
        <TextInput
          placeholder="Поиск"
          leftSection={<IconSearch size={14} />}
          rightSection={
            search ? (
              <ActionIcon variant="subtle" onClick={() => setSearch('')}>
                <IconX size={14} />
              </ActionIcon>
            ) : null
          }
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          autoFocus
        />
        <ScrollArea h={400}>
          <Stack gap="xs">
            {filtered.map((it) => (
              <Card
                key={it.id}
                withBorder
                padding="sm"
                radius="sm"
                style={{
                  cursor: 'pointer',
                  borderColor: it.id === selectedId ? '#228be6' : undefined,
                }}
                onClick={() => {
                  onSelect(it);
                }}
              >
                <Group justify="space-between" wrap="nowrap" gap="xs">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={500} truncate>
                      {it.label}
                    </Text>
                    {it.subline && (
                      <Text size="xs" c="dimmed" truncate>
                        {it.subline}
                      </Text>
                    )}
                    {it.details && (
                      <Text size="xs" c="dimmed" truncate>
                        {it.details}
                      </Text>
                    )}
                  </div>
                </Group>
              </Card>
            ))}
            {filtered.length === 0 && (
              <Text c="dimmed" size="sm" ta="center" py="md">
                {items.length === 0 ? emptyText : 'Ничего не найдено'}
              </Text>
            )}
          </Stack>
        </ScrollArea>
      </Stack>
    </Modal>
  );
}
